import React from "react";
import { useTranslation } from "react-i18next";

/**
 * A combined range-slider + number-input for a bounded numeric setting.
 * Passing an empty string as `value` signals "unset" (widget default),
 * which shows a Reset button once the user makes a change.
 *
 * Props:
 *  label       – visible field label
 *  description – optional helper text
 *  settingKey  – key passed back through onChange(key, value)
 *  min / max / step – range constraints
 *  defaultVal  – the widget's built-in default, shown as placeholder/hint
 *  value       – current value ("" = use widget default)
 *  unit        – optional suffix displayed next to the input (e.g. "px")
 *  onChange    – (settingKey, value) => void
 */
export default function GlassSlider({
  label,
  description,
  settingKey,
  min,
  max,
  step,
  defaultVal,
  value,
  unit = "",
  onChange,
}) {
  const { t } = useTranslation();
  const isEmpty = value === "" || value === null || value === undefined;
  const numVal = isEmpty ? defaultVal : Number(value);

  const handleSlider = (e) => onChange(settingKey, Number(e.target.value));
  const handleNumber = (e) => {
    const raw = e.target.value;
    if (raw === "") {
      onChange(settingKey, "");
      return;
    }
    const n = Number(raw);
    if (!isNaN(n) && n >= min && n <= max) onChange(settingKey, n);
  };

  return (
    <div className="mb-3">
      <div className="d-flex align-items-center justify-content-between mb-1">
        <label className="form-label mb-0 small fw-medium text-dark">
          {label}
        </label>
        {!isEmpty && (
          <button
            type="button"
            className="btn btn-link p-0 small text-muted"
            style={{ fontSize: "0.7rem" }}
            onClick={() => onChange(settingKey, "")}
          >
            {t("common.reset", "Reset")}
          </button>
        )}
      </div>
      {description && (
        <p className="small text-muted mb-2" style={{ fontSize: "0.75rem" }}>
          {description}
        </p>
      )}
      <div className="d-flex align-items-center gap-3">
        <input
          type="range"
          className="form-range flex-grow-1"
          style={{ maxWidth: 220 }}
          min={min}
          max={max}
          step={step}
          value={numVal}
          onChange={handleSlider}
        />
        <div
          className="d-flex align-items-center gap-1"
          style={{ minWidth: 90 }}
        >
          <input
            type="number"
            className="form-control form-control-sm text-center"
            style={{ width: 72, fontFamily: "monospace" }}
            min={min}
            max={max}
            step={step}
            value={isEmpty ? "" : numVal}
            placeholder={String(defaultVal)}
            onChange={handleNumber}
          />
          {unit && <span className="small text-muted">{unit}</span>}
        </div>
        <span
          className="small text-muted"
          style={{ fontSize: "0.7rem", minWidth: 80 }}
        >
          {t("common.default", "Default")}: {defaultVal}
          {unit}
        </span>
      </div>
    </div>
  );
}
